/**
 * API Service for OPENYC Backend
 * 封装后端 API 调用，支持 Firebase ID Token 认证
 */
import axios from 'axios';
import { io } from 'socket.io-client';
import { getFirebaseToken, onAuthStateChange, signInAnonymouslyUser } from './services/firebaseAuth';

// API Configuration - Supports multiple deployment environments
const ENV = process.env.NODE_ENV || 'development';

const API_BASE_URL = (() => {
  if (ENV === 'production') {
    const hostname = window.location.hostname;
    if (hostname === 'staging.openyc.vip' || hostname === 'staging.openyc.pages.dev') {
      return 'https://openyc-api-staging.fly.dev';
    }
    return 'https://openyc-api-backend.fly.dev';
  }
  return window.__api_base_url || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';
})();

const API_VERSION = '/api/v1';

// Build full API URL
// eslint-disable-next-line no-unused-vars
const getApiUrl = (path) => `${API_BASE_URL}${API_VERSION}${path}`;

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.socket = null;
    this.initialized = false;

    // Default axios instance without auth
    this.axios = axios.create({
      baseURL: this.baseURL,
      headers: {}
    });
  }

  /**
   * Initialize authentication
   * Sets up Firebase auth state listener and anonymous login if needed
   */
  async initialize() {
    if (this.initialized) return;

    const storedToken = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (storedToken) {
      this.axios.defaults.headers['Authorization'] = `Bearer ${storedToken}`;
      console.log('✅ Auth headers set from localStorage');
    }

    this.initialized = true;
  }

  /**
   * Refresh axios auth headers with fresh Firebase token
   */
  async refreshAxiosAuth() {
    try {
      const token = await getFirebaseToken();
      this.axios.defaults.headers['Authorization'] = `Bearer ${token}`;
      this.axios.defaults.headers['Content-Type'] = 'application/json';

      // Also update socket auth
      if (this.socket) {
        this.socket.auth = { token };
      }

      console.log('✅ Auth headers refreshed');
      return true;
    } catch (error) {
      console.error('❌ Failed to refresh auth:', error);
      return false;
    }
  }

  /**
   * Clear authentication headers
   */
  clearAuthHeaders() {
    delete this.axios.defaults.headers['Authorization'];
    delete this.axios.defaults.headers['Content-Type'];

    if (this.socket) {
      this.socket.auth = {};
    }
  }

  initSocket() {
    if (this.socket) return;

    console.log('[API] Initializing socket connection to:', this.baseURL);
    this.socket = io(this.baseURL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      auth: {}
    });

    this.socket.on('connect', () => {
      console.log('✅ Socket connected, id:', this.socket.id);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected, reason:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connect error:', error.message);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('✅ Socket reconnected after', attemptNumber, 'attempts');
    });
  }

  joinUserRoom(userId) {
    if (!this.socket) {
      this.initSocket();
    }
    
    const doJoin = () => {
      console.log('[API] Joining user room:', userId);
      this.socket.emit('join-user', userId);
    };
    
    if (this.socket.connected) {
      doJoin();
    } else {
      this.socket.once('connect', doJoin);
    }
  }

  joinChatRoom(chatId) {
    if (!this.socket) {
      this.initSocket();
    }
    
    const doJoin = () => {
      console.log('[API] Joining chat room:', chatId);
      this.socket.emit('join-chat', chatId);
    };
    
    if (this.socket.connected) {
      doJoin();
    } else {
      this.socket.once('connect', doJoin);
    }
  }

  onPrivateMessage(callback) {
    if (!this.socket) {
      this.initSocket();
    }
    console.log('[API] Registering private_message listener');
    this.socket.on('private_message', (data) => {
      console.log('[API] Received private_message:', data);
      callback(data);
    });
  }

  // ==================== Auth (Email Verification) ====================
  sendEmailVerificationCode(email) {
    return this.axios.post(`${API_VERSION}/auth/send-code`, { email })
      .then(response => {
        if (response.data.success) {
          console.log('✅ Verification code sent to:', email);
          return response.data.code;
        }
        throw new Error(response.data.error || '发送验证码失败');
      });
  }

  verifyEmailCode(email, code) {
    return this.axios.post(`${API_VERSION}/auth/verify-code`, { email, code })
      .then(response => {
        if (response.data.success) {
          console.log('✅ Email verification successful:', email);
          return response.data.token;
        }
        throw new Error(response.data.error || '验证码错误或已过期');
      });
  }

  async getCurrentUser() {
    try {
      const response = await this.axios.get(`${API_VERSION}/users/me`);
      if (response.data.success) {
        return response.data.user;
      }
      throw new Error(response.data.error || '获取用户信息失败');
    } catch (error) {
      console.error('❌ 获取用户信息失败:', error);
      throw error;
    }
  }

  async updateUserSettings(settings) {
    try {
      const response = await this.axios.put(`${API_VERSION}/users/me/settings`, { settings });
      if (response.data.success) {
        return response.data.user;
      }
      throw new Error(response.data.error || '更新设置失败');
    } catch (error) {
      console.error('❌ 更新设置失败:', error);
      throw error;
    }
  }

  async updateAvatar(avatar) {
    try {
      const response = await this.axios.put(`${API_VERSION}/users/me/avatar`, { avatar });
      if (response.data.success) {
        return response.data.user;
      }
      throw new Error(response.data.error || '更新头像失败');
    } catch (error) {
      console.error('❌ 更新头像失败:', error);
      throw error;
    }
  }

  async updateUserProfile(userId, data) {
    try {
      const response = await this.axios.put(`${API_VERSION}/friends/profile/${userId}`, data);
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || '更新资料失败');
    } catch (error) {
      console.error('❌ 更新用户资料失败:', error);
      throw error;
    }
  }

  logout() {
    this.clearAuthHeaders();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // ==================== Messages ====================

  async sendMessage(chatId, senderId, senderName, content, isAI = false) {
    try {
      const response = await this.axios.post(`${API_VERSION}/messages`, {
        chatId,
        senderId,
        senderName,
        content,
        isAI
      });
      if (response.data.success) {
        return response.data.message;
      }
      throw new Error(response.data.error || '发送消息失败');
    } catch (error) {
      console.error('❌ 发送消息失败:', error);
      throw error;
    }
  }

  async getMessages(chatId, limit = 50) {
    try {
      const response = await this.axios.get(`${API_VERSION}/messages/${chatId}`, { params: { limit } });
      if (response.data.success) {
        return response.data.messages;
      }
      throw new Error(response.data.error || '获取消息失败');
    } catch (error) {
      console.error('❌ 获取消息失败:', error);
      throw error;
    }
  }

  onNewMessage(callback) {
    if (!this.socket) {
      this.initSocket();
    }
    console.log('[API] Registering message listener');
    this.socket.on('message', (data) => {
      console.log('[API] Received message:', data);
      callback(data);
    });
  }

  onFriendRequest(callback) {
    if (!this.socket) {
      this.initSocket();
    }
    console.log('[API] Registering friend_request listener');
    this.socket.on('friend_request', (data) => {
      console.log('[API] Received friend_request:', data);
      callback(data);
    });
  }

  onSystemMessage(callback) {
    if (!this.socket) {
      this.initSocket();
    }
    console.log('[API] Registering system_message listener');
    this.socket.on('system_message', (data) => {
      console.log('[API] Received system_message:', data);
      callback(data);
    });
  }

  // ==================== Groups ====================

  async createGroup(name, ownerId, selectedSenders = []) {
    try {
      const response = await this.axios.post(`${API_VERSION}/groups/create`, {
        name,
        ownerId,
        selectedSenders
      });
      if (response.data.success) {
        return response.data.group;
      }
      throw new Error(response.data.error || '创建群组失败');
    } catch (error) {
      console.error('❌ 创建群组失败:', error);
      throw error;
    }
  }

  async getAvailableGroups(userId) {
    try {
      const params = userId ? { userId } : {};
      const response = await this.axios.get(`${API_VERSION}/groups`, { params });
      if (response.data.success) {
        return response.data.groups;
      }
      throw new Error(response.data.error || '获取群组失败');
    } catch (error) {
      console.error('❌ 获取群组失败:', error);
      throw error;
    }
  }

  async getUserGroups(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/groups/user/${userId}`);
      if (response.data.success) {
        return response.data.groups;
      }
      throw new Error(response.data.error || '获取用户群组失败');
    } catch (error) {
      console.error('❌ 获取用户群组失败:', error);
      throw error;
    }
  }

  async joinGroup(groupId, userId) {
    try {
      const response = await this.axios.post(`${API_VERSION}/groups/${groupId}/join`, {
        userId
      });
      if (response.data.success) {
        return response.data.group;
      }
      throw new Error(response.data.error || '加入群组失败');
    } catch (error) {
      console.error('❌ 加入群组失败:', error);
      throw error;
    }
  }

  async leaveGroup(groupId, userId) {
    try {
      const response = await this.axios.post(`${API_VERSION}/groups/${groupId}/leave`, {
        userId
      });
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || '退出群组失败');
    } catch (error) {
      console.error('❌ 退出群组失败:', error);
      throw error;
    }
  }

  async getGroupById(groupId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/groups/${groupId}`);
      if (response.data.success) {
        return response.data.group;
      }
      throw new Error(response.data.error || '获取群组失败');
    } catch (error) {
      console.error('❌ 获取群组失败:', error);
      throw error;
    }
  }

  async getGroupMessages(groupId, limit = 50, offset = 0) {
    try {
      const response = await this.axios.get(`${API_VERSION}/groups/${groupId}/messages`, {
        params: { limit, offset }
      });
      if (response.data.success) {
        return response.data.messages;
      }
      throw new Error(response.data.error || '获取消息失败');
    } catch (error) {
      console.error('❌ 获取群消息失败:', error);
      throw error;
    }
  }

  async sendGroupMessage(groupId, senderId, senderName, senderAvatar, content, type = 'text', mediaUrl = null) {
    try {
      const response = await this.axios.post(`${API_VERSION}/groups/${groupId}/messages`, {
        senderId,
        senderName,
        senderAvatar,
        content,
        type,
        mediaUrl
      });
      if (response.data.success) {
        return response.data.message;
      }
      throw new Error(response.data.error || '发送消息失败');
    } catch (error) {
      console.error('❌ 发送群消息失败:', error);
      throw error;
    }
  }

  async deleteMessage(messageId, userId) {
    try {
      const response = await this.axios.delete(`${API_VERSION}/messages/${messageId}`, {
        data: { userId }
      });
      if (response.data.success) {
        return response.data.result;
      }
      throw new Error(response.data.error || '删除消息失败');
    } catch (error) {
      console.error('❌ 删除消息失败:', error);
      throw error;
    }
  }

  async recallMessage(messageId, userId) {
    try {
      const response = await this.axios.post(`${API_VERSION}/messages/${messageId}/recall`, {
        userId
      });
      if (response.data.success) {
        return response.data.result;
      }
      throw new Error(response.data.error || '撤回消息失败');
    } catch (error) {
      console.error('❌ 撤回消息失败:', error);
      throw error;
    }
  }

  async deleteGroupMessage(groupId, messageId, userId) {
    try {
      const response = await this.axios.delete(`${API_VERSION}/groups/${groupId}/messages/${messageId}`, {
        data: { userId }
      });
      if (response.data.success) {
        return response.data.result;
      }
      throw new Error(response.data.error || '删除群消息失败');
    } catch (error) {
      console.error('❌ 删除群消息失败:', error);
      throw error;
    }
  }

  async recallGroupMessage(groupId, messageId, userId) {
    try {
      const response = await this.axios.post(`${API_VERSION}/groups/${groupId}/messages/${messageId}/recall`, {
        userId
      });
      if (response.data.success) {
        return response.data.result;
      }
      throw new Error(response.data.error || '撤回群消息失败');
    } catch (error) {
      console.error('❌ 撤回群消息失败:', error);
      throw error;
    }
  }

  onGroupCreated(callback) {
    if (!this.socket) {
      this.initSocket();
    }
    this.socket.on('group-created', callback);
  }

  async autoAssignAIGroup(userId, userName) {
    try {
      const response = await this.axios.post(`${API_VERSION}/groups/auto-assign`, {
        userId,
        userName
      });
      if (response.data.success) {
        return response.data.group;
      }
      throw new Error(response.data.error || '自动分配群聊失败');
    } catch (error) {
      console.error('❌ 自动分配AI群聊失败:', error);
      throw error;
    }
  }

  async getAllGroups() {
    try {
      const response = await this.axios.get(`${API_VERSION}/groups/all/list`);
      if (response.data.success) {
        return response.data.groups;
      }
      throw new Error(response.data.error || '获取所有群组失败');
    } catch (error) {
      console.error('❌ 获取所有群组失败:', error);
      throw error;
    }
  }

  async getRandomAIGroups(excludeIds = [], limit = 3) {
    try {
      const response = await this.axios.get(`${API_VERSION}/groups/random/ai-groups`, {
        params: { excludeIds: JSON.stringify(excludeIds), limit }
      });
      if (response.data.success) {
        return response.data.groups;
      }
      throw new Error(response.data.error || '获取随机AI群聊失败');
    } catch (error) {
      console.error('❌ 获取随机AI群聊失败:', error);
      throw error;
    }
  }

  async requestJoinGroup(groupId, userId, userName, targetUserId) {
    try {
      const response = await this.axios.post(`${API_VERSION}/groups/${groupId}/request-join`, {
        userId,
        userName,
        targetUserId
      });
      if (response.data.success) {
        return response.data.request;
      }
      throw new Error(response.data.error || '申请加入失败');
    } catch (error) {
      console.error('❌ 申请加入群聊失败:', error);
      throw error;
    }
  }

  async approveJoinRequest(groupId, requestUserId, approved) {
    try {
      if (approved) {
        const response = await this.axios.post(`${API_VERSION}/groups/${groupId}/join`, {
          userId: requestUserId
        });
        if (response.data.success) {
          return response.data.group;
        }
        throw new Error(response.data.error || '批准加入失败');
      } else {
        return { success: true, message: '已拒绝加入请求' };
      }
    } catch (error) {
      console.error('❌ 处理加入请求失败:', error);
      throw error;
    }
  }

  async getFriends(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/friends/${userId}`);
      if (response.data.success) {
        return response.data.friends;
      }
      throw new Error(response.data.error || '获取好友列表失败');
    } catch (error) {
      console.error('❌ 获取好友列表失败:', error);
      throw error;
    }
  }

  async getPendingFriendRequests(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/friends/${userId}/pending`);
      if (response.data.success) {
        return response.data.requests;
      }
      throw new Error(response.data.error || '获取好友请求失败');
    } catch (error) {
      console.error('❌ 获取好友请求失败:', error);
      throw error;
    }
  }

  async searchUser(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/friends/search/${userId}`);
      if (response.data.success) {
        return response.data.user;
      }
      throw new Error(response.data.error || '用户不存在');
    } catch (error) {
      console.error('❌ 搜索用户失败:', error);
      throw error;
    }
  }

  async addFriend(userId, friendId) {
    try {
      const response = await this.axios.post(`${API_VERSION}/friends/add`, {
        userId,
        friendId
      });
      if (response.data.success) {
        return response.data.friend;
      }
      throw new Error(response.data.error || '添加好友失败');
    } catch (error) {
      console.error('❌ 添加好友失败:', error);
      throw error;
    }
  }

  async acceptFriend(userId, friendId) {
    try {
      const response = await this.axios.post(`${API_VERSION}/friends/accept`, {
        userId,
        friendId
      });
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || '接受好友请求失败');
    } catch (error) {
      console.error('❌ 接受好友请求失败:', error);
      throw error;
    }
  }

  async deleteFriend(userId, friendId) {
    try {
      const response = await this.axios.delete(`${API_VERSION}/friends/${userId}/${friendId}`);
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || '删除好友失败');
    } catch (error) {
      console.error('❌ 删除好友失败:', error);
      throw error;
    }
  }

  async getPrivateMessages(chatId, limit = 50, offset = 0) {
    try {
      const response = await this.axios.get(`${API_VERSION}/friends/chat/${chatId}/messages`, {
        params: { limit, offset }
      });
      if (response.data.success) {
        return response.data.messages;
      }
      throw new Error(response.data.error || '获取消息失败');
    } catch (error) {
      console.error('❌ 获取私聊消息失败:', error);
      throw error;
    }
  }

  async sendPrivateMessage(chatId, senderId, senderName, senderAvatar, content, type = 'text', mediaUrl = null) {
    try {
      const response = await this.axios.post(`${API_VERSION}/friends/chat/${chatId}/messages`, {
        senderId,
        senderName,
        senderAvatar,
        content,
        type,
        mediaUrl
      });
      if (response.data.success) {
        return response.data.message;
      }
      throw new Error(response.data.error || '发送消息失败');
    } catch (error) {
      console.error('❌ 发送私聊消息失败:', error);
      throw error;
    }
  }

  async deletePrivateMessage(chatId, messageId, userId) {
    try {
      const response = await this.axios.delete(`${API_VERSION}/friends/chat/${chatId}/messages/${messageId}`, {
        data: { userId }
      });
      if (response.data.success) {
        return response.data.result;
      }
      throw new Error(response.data.error || '删除消息失败');
    } catch (error) {
      console.error('❌ 删除私聊消息失败:', error);
      throw error;
    }
  }

  async recallPrivateMessage(chatId, messageId, userId) {
    try {
      const response = await this.axios.post(`${API_VERSION}/friends/chat/${chatId}/messages/${messageId}/recall`, {
        userId
      });
      if (response.data.success) {
        return response.data.result;
      }
      throw new Error(response.data.error || '撤回消息失败');
    } catch (error) {
      console.error('❌ 撤回私聊消息失败:', error);
      throw error;
    }
  }

  async getAIReply(userId, friendId, message, targetUserName) {
    try {
      const response = await this.axios.post(`${API_VERSION}/friends/ai-reply`, {
        userId,
        friendId,
        message,
        targetUserName
      });
      if (response.data.success) {
        return {
          reply: response.data.reply,
          avatarName: response.data.avatarName
        };
      }
      throw new Error(response.data.error || '获取AI回复失败');
    } catch (error) {
      console.error('❌ 获取AI回复失败:', error);
      throw error;
    }
  }

  async setAutoReply(userId, friendId, enabled) {
    try {
      const response = await this.axios.put(`${API_VERSION}/friends/auto-reply/${userId}/${friendId}`, {
        enabled
      });
      return response.data;
    } catch (error) {
      console.error('❌ 设置自动回复失败:', error);
      throw error;
    }
  }

  async getNotifications(userId, unreadOnly = true) {
    try {
      const response = await this.axios.get(`${API_VERSION}/friends/${userId}/notifications`, {
        params: { unreadOnly }
      });
      return response.data.notifications || [];
    } catch (error) {
      console.error('❌ 获取通知失败:', error);
      return [];
    }
  }

  async markNotificationsRead(userId, notificationId = null) {
    try {
      await this.axios.post(`${API_VERSION}/friends/${userId}/notifications/read`, {
        notificationId
      });
    } catch (error) {
      console.error('❌ 标记通知已读失败:', error);
    }
  }

  // ==================== OpenClaw Integration ====================

  async getAgents() {
    try {
      const response = await this.axios.get(`${API_VERSION}/openclaw/agents`);
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || '获取 agents 失败');
    } catch (error) {
      console.error('❌ 获取 agents 失败:', error);
      throw error;
    }
  }

  async executeAgent(agentId, action, params = {}) {
    try {
      const response = await this.axios.post(`${API_VERSION}/openclaw/agent/execute`, {
        agentId,
        action,
        params
      });
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || '执行 agent 失败');
    } catch (error) {
      console.error('❌ 执行 agent 失败:', error);
      throw error;
    }
  }

  async updateAgentConfig(agentCapabilities, agentConfig) {
    try {
      const response = await this.axios.put(`${API_VERSION}/openclaw/agent/config`, {
        agentCapabilities,
        agentConfig
      });
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || '更新 agent 配置失败');
    } catch (error) {
      console.error('❌ 更新 agent 配置失败:', error);
      throw error;
    }
  }

  async sendOpenClawWebhook(data) {
    try {
      const response = await this.axios.post(`/api/message/webhook`, data);
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error || '发送到 OpenClaw 失败');
    } catch (error) {
      console.error('❌ OpenClaw webhook 失败:', error);
      throw error;
    }
  }

  async sendOpenClawMessage(to, content, mediaId, type = 'text') {
    try {
      const response = await this.axios.post(`${API_VERSION}/messages/send`, {
        to,
        content,
        mediaId,
        type
      });
      if (response.data.success) {
        return response.data.message;
      }
      throw new Error(response.data.error || '发送消息失败');
    } catch (error) {
      console.error('❌ OpenClaw 发送消息失败:', error);
      throw error;
    }
  }

  async sendOpenClawMedia(to, mediaUrl, mediaType, caption = '', duration = 0) {
    try {
      const response = await this.axios.post(`${API_VERSION}/messages/send/media`, {
        to,
        mediaUrl,
        mediaType,
        caption,
        duration
      });
      if (response.data.success) {
        return response.data.message;
      }
      throw new Error(response.data.error || '发送媒体失败');
    } catch (error) {
      console.error('❌ OpenClaw 发送媒体失败:', error);
      throw error;
    }
  }

  async getUserProfile(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/user-profiles/${userId}`);
      if (response.data.success) {
        return response.data.profile;
      }
      throw new Error(response.data.error || '获取用户画像失败');
    } catch (error) {
      console.error('❌ 获取用户画像失败:', error);
      throw error;
    }
  }

  async analyzeUserProfile(userId) {
    try {
      const response = await this.axios.post(`${API_VERSION}/user-profiles/analyze/${userId}`);
      return response.data;
    } catch (error) {
      console.error('❌ 分析用户画像失败:', error);
      throw error;
    }
  }

  async getLearningStatus(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/user-profiles/learning-status/${userId}`);
      return response.data;
    } catch (error) {
      console.error('❌ 获取学习状态失败:', error);
      throw error;
    }
  }

  async updateAdaptationSettings(userId, settings) {
    try {
      const response = await this.axios.post(`${API_VERSION}/user-profiles/adaptation-settings/${userId}`, settings);
      return response.data;
    } catch (error) {
      console.error('❌ 更新适应设置失败:', error);
      throw error;
    }
  }

  async getUserInsights(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/user-profiles/insights/${userId}`);
      return response.data;
    } catch (error) {
      console.error('❌ 获取用户洞察失败:', error);
      throw error;
    }
  }

  async getMemories(userId, options = {}) {
    try {
      const { type, category, limit = 50 } = options;
      const params = { limit };
      if (type) params.type = type;
      if (category) params.category = category;

      const response = await this.axios.get(`${API_VERSION}/memories/${userId}`, { params });
      if (response.data.success) {
        return response.data.memories;
      }
      throw new Error(response.data.error || '获取记忆失败');
    } catch (error) {
      console.error('❌ 获取记忆失败:', error);
      throw error;
    }
  }

  async getMemoryStats(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/memories/stats/${userId}`);
      return response.data;
    } catch (error) {
      console.error('❌ 获取记忆统计失败:', error);
      throw error;
    }
  }

  async createMemory(userId, memoryData) {
    try {
      const response = await this.axios.post(`${API_VERSION}/memories/${userId}`, memoryData);
      if (response.data.success) {
        return response.data.memory;
      }
      throw new Error(response.data.error || '创建记忆失败');
    } catch (error) {
      console.error('❌ 创建记忆失败:', error);
      throw error;
    }
  }

  async updateMemory(memoryId, updates) {
    try {
      const response = await this.axios.put(`${API_VERSION}/memories/${memoryId}`, updates);
      if (response.data.success) {
        return response.data.memory;
      }
      throw new Error(response.data.error || '更新记忆失败');
    } catch (error) {
      console.error('❌ 更新记忆失败:', error);
      throw error;
    }
  }

  async deleteMemory(memoryId) {
    try {
      const response = await this.axios.delete(`${API_VERSION}/memories/${memoryId}`);
      return response.data;
    } catch (error) {
      console.error('❌ 删除记忆失败:', error);
      throw error;
    }
  }

  async clearAllMemories(userId) {
    try {
      const response = await this.axios.delete(`${API_VERSION}/memories/user/${userId}/all`);
      return response.data;
    } catch (error) {
      console.error('❌ 清空记忆失败:', error);
      throw error;
    }
  }

  // ==================== Subscription ====================

  async getSubscription(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/subscriptions/${userId}`);
      return response.data;
    } catch (error) {
      console.error('❌ 获取订阅信息失败:', error);
      throw error;
    }
  }

  async createSubscriptionOrder(userId, planType) {
    try {
      const response = await this.axios.post(`${API_VERSION}/subscriptions/${userId}/create-order`, { planType });
      return response.data;
    } catch (error) {
      console.error('❌ 创建订阅订单失败:', error);
      throw error;
    }
  }

  async checkLimits(userId) {
    try {
      const response = await this.axios.get(`${API_VERSION}/subscriptions/${userId}/check-limits`);
      return response.data;
    } catch (error) {
      console.error('❌ 检查限制失败:', error);
      throw error;
    }
  }

  // ==================== Socket 管理 ====================

  joinChat(chatId) {
    if (!this.socket) {
      this.initSocket();
    }
    this.socket.emit('join-chat', chatId);
  }
}

const api = new ApiService();

export default api;
